import {
  IllustratedCta,
  PackageWithFeaturesCta,
  SectionGroup,
  TestimonialShortStoryList,
  Timeline,
} from '@sleepstation/components'
import { CopyParagraph } from '@sleepstation/components-copy'
import { VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { LAYOUT_SPACING } from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import { marginH, mq } from '@sleepstation/style-api-helpers'
import { reversedTheme } from '@sleepstation/themes-public-site'
import { replaceTokens, toDayMonthVerboseUSA } from '@sleepstation/utils'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { assoc, lensProp, over } from 'ramda'
import pipe from 'ramda/src/pipe'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { formatByCurrency } from '../../../../utils/currency'
import BreakBehindBreakoutModuleMid from '../../../shared/breakoutModules/BreakBehindBreakoutModuleMid'
import BreakBehindBreakoutModuleWide from '../../../shared/breakoutModules/BreakBehindBreakoutModuleWide'
import BreakoutModuleNarrow from '../../../shared/breakoutModules/BreakoutModuleNarrow'
import BreakoutModuleWide from '../../../shared/breakoutModules/BreakoutModuleWide'
import ChartSection from './ChartSection'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PROJECTION_INTERVAL = 21

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const lensCardData = lensProp(`cardData`)

const overCardData = over(lensCardData)

const formatPrice = (template, { code, value }) => {
  const priceWithCurrency = formatByCurrency(code, value)
  return replaceTokens(template, { priceWithCurrency })
}

const createSubtitleFromTemplate = (substitutions) => (o) => {
  const { subtitleTemplate } = o
  const subtitle = formatPrice(subtitleTemplate, substitutions)
  return assoc(`subtitle`, subtitle, o)
}

const createLinkLabelFromTemplate = (substitutions) => (o) => {
  const { linkLabelTemplate } = o
  const linkLabel = formatPrice(linkLabelTemplate, substitutions)
  return assoc(`linkLabel`, linkLabel, o)
}

const createLinkUrlFromTemplate = (substitutions) => (o) => {
  const { linkUrlTemplate } = o
  const linkUrl = replaceTokens(linkUrlTemplate, substitutions)
  return assoc(`linkUrl`, linkUrl, o)
}

const injectDateInFourWeeks = (template) => {
  const dateInFourWeeks = dayjs().add(PROJECTION_INTERVAL, `days`)
  const formattedDate = toDayMonthVerboseUSA(dateInFourWeeks)
  const footerBodyWithDate = replaceTokens(template, {
    date: formattedDate,
  })

  return footerBodyWithDate
}

const injectDateInFourWeeksAndMakeAst = pipe(injectDateInFourWeeks, parseToAst)

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Inner = styled.div`
  ${marginH([-1, 0])}
`

const ImageWrapper = styled.div`
  > img {
    width: 100%;
    display: block;
  }

  > *:first-child {
    ${mq(`smallUp`)} {
      display: none;
    }
  }

  > *:last-child {
    display: none;
    ${mq(`smallUp`)} {
      display: block;
    }
  }
`

// -----------------------------------------------------------------------------
// Factories
// -----------------------------------------------------------------------------
/* eslint-disable react/display-name, react/prop-types */

const buildPackageWithFeaturesCtaSection =
  (margin, name, dataCy) =>
  ({ pageContent, product }) => {
    const { key, prices } = product

    const {
      value,
      currency: { code },
    } = prices[0]

    const props = pageContent[name]

    const transformedProps = pipe(
      createLinkUrlFromTemplate({ key }),
      createLinkLabelFromTemplate({ value, code }),
      overCardData(createSubtitleFromTemplate({ value, code }))
    )(props)

    return (
      <BreakBehindBreakoutModuleWide
        hasBottomBorder={false}
        hasGradient={false}
        marginV={margin}
        paddingV={[[0.5, 0], [0.5, 0], 0.5]}
      >
        <PackageWithFeaturesCta
          {...transformedProps}
          forceOpenInSameWindow
          dataCy={dataCy}
        />
      </BreakBehindBreakoutModuleWide>
    )
  }

const buildChartSection = ({ pageContent, projectedSleepEfficiencyData }) => {
  const { title, bodyAst, footer } =
    pageContent.sleepEfficiencyProgressChartSection

  const footerTitle = injectDateInFourWeeks(footer.titleTemplate)
  const footerBodyAst = injectDateInFourWeeksAndMakeAst(footer.bodyTemplate)

  return (
    <BreakoutModuleNarrow
      marginV={[
        [1, 1],
        [2, 1],
        [2, 1],
        [2, 1.5],
      ]}
      hasBottomBorder={false}
      hasGradient={false}
    >
      <ChartSection
        title={title}
        bodyAst={bodyAst}
        data={projectedSleepEfficiencyData}
        footerTitle={footerTitle}
        footerBodyAst={footerBodyAst}
      />
    </BreakoutModuleNarrow>
  )
}

const buildPackageWithFeaturesCtaSectionTop =
  buildPackageWithFeaturesCtaSection(
    [[1, 0]],
    `packageWithFeaturesCtaSectionTop`,
    `packageWithFeaturesCtaSectionTop`
  )

const buildIllusratedHeaderSection = ({ pageContent }) => {
  const { title, bodyAst, imageAlt, svgUrlXSmall, svgUrlSmallUp } =
    pageContent.illustratedHeaderSection

  return (
    <BreakoutModuleNarrow
      hasBottomBorder={false}
      hasGradient={false}
      marginV={[
        [1.5, 2],
        [1.5, 3],
        [2.5, 4],
      ]}
    >
      <VLayout
        spacing={[
          LAYOUT_SPACING.LARGE,
          LAYOUT_SPACING.LARGE,
          LAYOUT_SPACING.X_LARGE,
        ]}
      >
        <ImageWrapper>
          <img alt={imageAlt} src={svgUrlXSmall} />
          <img alt={imageAlt} src={svgUrlSmallUp} />
        </ImageWrapper>
        <SectionGroup title={title} isClose>
          <CopyRenderer htmlAst={bodyAst} />
        </SectionGroup>
      </VLayout>
    </BreakoutModuleNarrow>
  )
}

const buildTimelineSection = ({ pageContent }) => {
  const { timeline } = pageContent.timelineSection

  return (
    <BreakoutModuleWide hasBottomBorder={false} hasGradient={false}>
      <Timeline steps={timeline} />
    </BreakoutModuleWide>
  )
}

const buildIllustratedCtaSection = ({ pageContent, product }) => {
  const {
    imageUrlSmallDown,
    imageUrlMediumUp,
    title,
    body,
    linkLabel,
    linkUrlTemplate,
  } = pageContent.illustratedCtaSection

  const { key } = product

  const linkUrl = replaceTokens(linkUrlTemplate, { key })

  return (
    <BreakBehindBreakoutModuleMid
      hasBottomBorder={false}
      hasGradient={false}
      marginV={[
        [1.5, 0],
        [2, 0],
        [3, 0],
      ]}
      paddingV={[0, 0, [2.75, 0], [3, 0]]}
    >
      <ThemeProvider theme={reversedTheme}>
        <IllustratedCta
          imageSmallDown={imageUrlSmallDown}
          imageMediumUp={imageUrlMediumUp}
          title={title}
          body={body}
          linkLabel={linkLabel}
          linkUrl={linkUrl}
          forceOpenInSameWindow
          dataCy="illustratedCta"
        />
      </ThemeProvider>
    </BreakBehindBreakoutModuleMid>
  )
}

const buildPatientShortStoriesSection = ({ pageContent }) => {
  const { title, body, data } = pageContent.patientShortStoriesSection

  return (
    <BreakoutModuleNarrow
      hasBottomBorder={false}
      hasGradient={false}
      marginV={[
        [1, 0],
        [2, 0],
      ]}
      paddingV={[0, 0.5]}
    >
      <VLayout spacing={LAYOUT_SPACING.LARGE}>
        <SectionGroup title={title} isClose>
          <CopyParagraph>{body}</CopyParagraph>
        </SectionGroup>
        <Inner>
          <TestimonialShortStoryList stories={data} />
        </Inner>
      </VLayout>
    </BreakoutModuleNarrow>
  )
}

const buildPackageWithFeaturesCtaSectionBottom =
  buildPackageWithFeaturesCtaSection(
    [
      [1, 0],
      [1, 5],
      [3, 5],
      [4, 5],
    ],
    `packageWithFeaturesCtaSectionBottom`,
    `packageWithFeaturesCtaSectionBottom`
  )

/* eslint-enable react/display-name, react/prop-types */

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(VLayout).attrs({ spacing: LAYOUT_SPACING.LARGE })``

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const UpgradePage = ({
  pageContent,
  projectedSleepEfficiencyData,
  currentEnrolment,
}) => {
  const { upgradeProducts } = currentEnrolment
  const product = upgradeProducts[0]

  return (
    <Layout>
      {buildChartSection({ pageContent, projectedSleepEfficiencyData })}
      {buildPackageWithFeaturesCtaSectionTop({
        pageContent,
        product,
      })}
      {buildIllusratedHeaderSection({ pageContent })}
      {buildTimelineSection({ pageContent })}
      {buildIllustratedCtaSection({ pageContent, product })}
      {buildPatientShortStoriesSection({ pageContent })}
      {buildPackageWithFeaturesCtaSectionBottom({ pageContent, product })}
    </Layout>
  )
}

UpgradePage.propTypes = {
  pageContent: PropTypes.object.isRequired,
  projectedSleepEfficiencyData: PropTypes.array.isRequired,
  currentEnrolment: PropTypes.object.isRequired,
}

export default UpgradePage
